import {useMemo} from 'react'

import {restApi} from 'src/services/rest'
import type {BuildId} from 'src/types'

interface Props {
  buildId: BuildId
}

export function useQueryBuildParameters(props: Props) {
  const {buildId} = props

  const buildParametersResult = restApi.endpoints.getBuildProperties.useQuery(
    {
      buildLocator: `id:${buildId}`,
      fields:
        'composite,resultingProperties(property(name,value),$locator:(name:(value:(^dep\\..*),matchType:does-not-match))),startProperties(property(name,value),$locator:(name:(value:(^dep\\..*),matchType:does-not-match))),properties(**),originalProperties(**)',
    },
    {
      selectFromResult: ({data, isLoading}) => ({
        properties: data?.properties,
        resultingProperties: data?.resultingProperties,
        originalProperties: data?.originalProperties ?? data?.properties,
        startProperties: data?.startProperties ?? data?.resultingProperties,
        composite: data?.composite,
        isLoading,
      }),
    },
  )

  const state = useMemo(() => {
    const startProperties = buildParametersResult.composite
      ? buildParametersResult.resultingProperties
      : buildParametersResult.startProperties

    return {
      properties: buildParametersResult.properties,
      resultingProperties: buildParametersResult.resultingProperties,
      originalProperties: Object.fromEntries(
        buildParametersResult.originalProperties?.property?.map(
          property => [property.name, property.value] as [string, string],
        ) ?? [],
      ),
      startProperties: Object.fromEntries(
        startProperties?.property?.map(
          property => [property.name, property.value] as [string, string],
        ) ?? [],
      ),
      isLoading: buildParametersResult.isLoading,
    }
  }, [
    buildParametersResult.composite,
    buildParametersResult.isLoading,
    buildParametersResult.originalProperties?.property,
    buildParametersResult.properties,
    buildParametersResult.resultingProperties,
    buildParametersResult?.startProperties,
  ])

  return {
    state,
    fetch: buildParametersResult.refetch,
  }
}
